import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, NavLink } from 'react-router-dom';
import { logoutUser } from '../../features/auth/authSlice';
import { AppDispatch, RootState } from '../../app/store';
import { killSwitch } from '../../serviceWorkerRegistration';
// import SidenavDrawer from './SidenavDrawer'; // Import the SidenavDrawer component - Removed as it's unused

// Use SVG icons directly instead of @heroicons/react
const SunIcon = ({className = "h-5 w-5"}) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
  </svg>
);

const MoonIcon = ({className = "h-5 w-5"}) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
  </svg>
);

const Bars3Icon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
  </svg>
);

const XMarkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ArrowLeftOnRectangleIcon = ({className = "h-5 w-5 mr-1 inline-block"}) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75" />
  </svg>
);

interface NavbarProps {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ isDarkMode, toggleDarkMode }) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const canViewPurchaseOrders = user && user.user_role_id && ![1, 2, 3].includes(user.user_role_id);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setCanInstall(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    setDeferredPrompt(null);
    setCanInstall(false);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogoutClick = () => setShowLogoutConfirm(true);

  const handleLogoutConfirm = async () => {
    setShowLogoutConfirm(false);
    await handleLogout();
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const [isClearingCache, setIsClearingCache] = useState(false);
  const handleClearCache = async () => {
    setIsClearingCache(true);
    try {
      await killSwitch();
    } catch (e) {
      console.error('Clear cache failed:', e);
      window.location.reload();
    }
  };

  // const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen); // Removed as SidenavDrawer is removed

  if (!isAuthenticated) {
    return null; // Don't render Navbar if not authenticated (or show a limited version)
  }

  const navShellClass = isDarkMode
    ? 'backdrop-blur-xl bg-midnight-900/80 border-white/5 text-white'
    : 'backdrop-blur-xl bg-white/85 border-slate-200/80 text-slate-900 shadow-md';

  const activeDesktopLink = isDarkMode ? 'bg-white/15 text-white' : 'bg-slate-900/10 text-slate-900';
  const inactiveDesktopLink = isDarkMode
    ? 'text-slate-300 hover:text-white hover:bg-white/10'
    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/60';

  return (
    <>
      <nav className={`sticky top-0 z-50 ${navShellClass}`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 shadow-glow flex items-center justify-center text-lg font-bold text-white">
                CP
              </div>
              <div className="leading-tight">
                <Link to="/" className="text-xl font-semibold tracking-tight font-heading text-current">
                  Colefax PO
                </Link>
              </div>
            </div>

            {/* Mobile Menu Button - visible on small screens, hidden on medium and up */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`${isDarkMode ? 'text-white/80 hover:text-white' : 'text-slate-600 hover:text-slate-900'} focus:outline-none`}
                aria-label="Open navigation menu"
              >
                {isMobileMenuOpen ? <XMarkIcon /> : <Bars3Icon />}
              </button>
            </div>

            {/* Desktop Navigation Links - hidden on small screens, visible on medium and up */}
            <div className="hidden md:flex items-center space-x-4">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition ${
                    isActive ? activeDesktopLink : inactiveDesktopLink
                  }`
                }
              >
                Dashboard
              </NavLink>
              <NavLink
                to="/requisitions"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition ${
                    isActive
                      ? (isDarkMode ? 'bg-white/15 text-white' : 'bg-slate-900/10 text-slate-900')
                      : inactiveDesktopLink
                  }`
                }
              >
                Requisitions
              </NavLink>
              {/* Purchase Orders - only show for non-store/coach users */}
              {canViewPurchaseOrders && (
                <NavLink
                  to="/purchase-orders"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-full text-sm font-medium transition ${
                      isActive
                        ? (isDarkMode ? 'bg-white/15 text-white' : 'bg-slate-900/10 text-slate-900')
                        : inactiveDesktopLink
                    }`
                  }
                >
                  Purchase Orders
                </NavLink>
              )}
              <NavLink
                to="/maintenance-log"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition ${
                    isActive ? activeDesktopLink : inactiveDesktopLink
                  }`
                }
              >
                Maintenance
              </NavLink>
              {/* Admin link - only show for admin users */}
              {user?.is_admin && (
                <NavLink
                  to="/admin/sessions"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-full text-sm font-medium transition ${
                      isActive ? activeDesktopLink : inactiveDesktopLink
                    }`
                  }
                >
                  Admin
                </NavLink>
              )}
              {/* Install App Button - only show if installable */}
              {canInstall && (
                <button
                  onClick={handleInstallClick}
                  className="px-3 py-1 rounded-full text-sm font-medium transition bg-blue-500 hover:bg-blue-600 text-white flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Install
                </button>
              )}
              {user && (
                <Link
                  to="/profile"
                  className={`flex items-center space-x-0 px-2 py-1 rounded-full transition hover:opacity-80 ${isDarkMode ? 'bg-white/5' : 'bg-slate-100 border border-slate-200'}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-accent-500/10 border border-accent-400/30' : 'bg-accent-500/15 border border-accent-500/40'}`}>
                    <span className={`${isDarkMode ? 'text-accent-200' : 'text-accent-700'} font-semibold text-sm`}>
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="hidden md:flex flex-col">
                    <span className={`text-sm mx-1 font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{user.name}</span>
                  </div>
                </Link>
              )}

              {/* Dark Mode Toggle Button */}
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-full focus:outline-none focus-visible:ring-2 ${
                  isDarkMode
                    ? 'hover:bg-white/10 focus-visible:ring-white/60'
                    : 'hover:bg-slate-200/70 focus-visible:ring-slate-500/40'
                }`}
                aria-label={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDarkMode ? (
                  <SunIcon />
                ) : (
                  <MoonIcon />
                )}
              </button>

              {/* Clear Cache / Force Update Button */}
              <button
                onClick={handleClearCache}
                disabled={isClearingCache}
                className={`p-2 rounded-full focus:outline-none focus-visible:ring-2 disabled:opacity-50 ${
                  isDarkMode
                    ? 'hover:bg-white/10 focus-visible:ring-white/60'
                    : 'hover:bg-slate-200/70 focus-visible:ring-slate-500/40'
                }`}
                aria-label="Clear cache and force update"
                title="Clear cache and force update"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>

              {/* Logout Button */}
              <button
                onClick={handleLogoutClick}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-400 to-brand-500 hover:from-brand-300 hover:to-brand-500 text-white font-semibold py-2 px-4 rounded-full text-sm shadow-lg shadow-brand-900/40 transition-all"
              >
                <ArrowLeftOnRectangleIcon />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Content */}
      {isMobileMenuOpen && (
        <div className={`md:hidden fixed top-20 inset-x-0 backdrop-blur-xl p-4 space-y-1 sm:px-6 z-40 shadow-soft border-b ${
          isDarkMode ? 'bg-midnight-900/95 border-white/10' : 'bg-white/95 border-slate-200'
        }`}>
          <NavLink
            to="/"
            className={({ isActive }) =>
              `block px-4 py-2 rounded-lg text-base font-medium transition ${
                isActive
                  ? (isDarkMode ? 'bg-white/15 text-white' : 'bg-slate-900/10 text-slate-900')
                  : (isDarkMode ? 'text-slate-300 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-200/70 hover:text-slate-900')
              }`
            }
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Dashboard
          </NavLink>
          <NavLink
            to="/requisitions"
            className={({ isActive }) =>
              `block px-4 py-2 rounded-lg text-base font-medium transition ${
                isActive
                  ? (isDarkMode ? 'bg-white/15 text-white' : 'bg-slate-900/10 text-slate-900')
                  : (isDarkMode ? 'text-slate-300 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-200/70 hover:text-slate-900')
              }`
            }
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Requisitions
          </NavLink>
          {/* Purchase Orders - only show for non-store/coach users */}
          {canViewPurchaseOrders && (
            <NavLink
              to="/purchase-orders"
              className={({ isActive }) =>
                `block px-4 py-2 rounded-lg text-base font-medium transition ${
                  isActive
                    ? (isDarkMode ? 'bg-white/15 text-white' : 'bg-slate-900/10 text-slate-900')
                    : (isDarkMode ? 'text-slate-300 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-200/70 hover:text-slate-900')
                }`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Purchase Orders
            </NavLink>
          )}
          <NavLink
            to="/maintenance-log"
            className={({ isActive }) =>
              `block px-4 py-2 rounded-lg text-base font-medium transition ${
                isActive
                  ? (isDarkMode ? 'bg-white/15 text-white' : 'bg-slate-900/10 text-slate-900')
                  : (isDarkMode ? 'text-slate-300 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-200/70 hover:text-slate-900')
              }`
            }
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Maintenance
          </NavLink>
          {/* Admin link for mobile - only show for admin users */}
          {user?.is_admin && (
            <NavLink
              to="/admin/sessions"
              className={({ isActive }) =>
                `block px-4 py-2 rounded-lg text-base font-medium transition ${
                  isActive
                    ? (isDarkMode ? 'bg-white/15 text-white' : 'bg-slate-900/10 text-slate-900')
                    : (isDarkMode ? 'text-slate-300 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-200/70 hover:text-slate-900')
                }`
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Admin
            </NavLink>
          )}
          <hr className={isDarkMode ? 'border-white/10' : 'border-slate-200'} />
          <div className="px-3 py-2">
            {user && (
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center space-x-3 mb-3 rounded-2xl px-3 py-2 transition hover:opacity-80 ${isDarkMode ? 'bg-white/5' : 'bg-slate-100 border border-slate-200'}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-accent-500/10 border border-accent-400/30' : 'bg-accent-500/15 border border-accent-500/40'}`}>
                  <span className={`${isDarkMode ? 'text-accent-200' : 'text-accent-700'} font-semibold text-base`}>
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{user.name}</span>
                </div>
              </Link>
            )}
          </div>
          <button
            onClick={() => { toggleDarkMode(); setIsMobileMenuOpen(false); }}
            className={`w-full text-left block px-4 py-2 rounded-lg text-base font-medium flex items-center ${
              isDarkMode ? 'text-slate-200 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-200/60'
            }`}
          >
            {isDarkMode ? (
              <SunIcon className="inline-block h-5 w-5 mr-2" />
            ) : (
              <MoonIcon className="inline-block h-5 w-5 mr-2" />
            )}
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
          <button
            onClick={() => { handleClearCache(); setIsMobileMenuOpen(false); }}
            disabled={isClearingCache}
            className={`w-full text-left block px-4 py-2 rounded-lg text-base font-medium flex items-center disabled:opacity-50 ${
              isDarkMode ? 'text-slate-200 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-200/60'
            }`}
          >
            <svg className="inline-block h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isClearingCache ? 'Clearing...' : 'Clear Cache'}
          </button>
          <button
            onClick={() => { handleLogoutClick(); setIsMobileMenuOpen(false); }}
            className="w-full text-left block px-4 py-2 rounded-lg text-base font-medium bg-gradient-to-r from-brand-400 to-brand-500 text-white flex items-center justify-center"
          >
            <ArrowLeftOnRectangleIcon className="inline-block h-5 w-5 mr-2" />
            Logout
          </button>
        </div>
      )}
      
      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleLogoutCancel}
          />
          
          {/* Modal */}
          <div className={`relative bg-white dark:bg-midnight-900 rounded-2xl shadow-2xl p-6 max-w-md mx-4 border ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
            <div className="text-center">
              {/* Icon */}
              <div className="mx-auto w-12 h-12 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center mb-4">
                <ArrowLeftOnRectangleIcon className="w-6 h-6 text-brand-600 dark:text-brand-400" />
              </div>
              
              {/* Title */}
              <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Confirm Logout
              </h3>
              
              {/* Message */}
              <p className={`mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Are you sure you want to log out? You'll need to sign in again to access your account.
              </p>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleLogoutCancel}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                    isDarkMode 
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogoutConfirm}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-brand-400 to-brand-500 hover:from-brand-300 hover:to-brand-500 text-white rounded-lg font-medium transition-all shadow-lg shadow-brand-900/40"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Removed SidenavDrawer component as it's no longer used */}
    </>
  );
};

export default Navbar;
